import { useEffect, useState } from 'react';
import { Sun, Moon } from 'lucide-react';

/**
 * Global light/dark toggle — promoted from four identical per-app copies
 * (command-center, playhouse, action-view, launcher) per the promotion rule
 * in mini-apps/CLAUDE.md. One theme state for all of Terra's mini-app
 * surfaces: flips the html `.dark` class and persists to
 * localStorage('theme'); `useMiniAppDarkMode` (both the /tools and /flow
 * hosts run it) applies the stored value on mount, so the toggle and the
 * hosts always agree.
 *
 * Under /flow this renders once in the shell tab bar and drives every app;
 * apps must not render their own copy alongside it.
 */
export function ThemeToggle() {
  const [isDark, setIsDark] = useState(() => document.documentElement.classList.contains('dark'));

  // Track the html.dark class rather than snapshotting it once: the host's
  // useMiniAppDarkMode applies the initial theme (incl. the
  // prefers-color-scheme fallback) in an effect AFTER this component's first
  // render, so a mount-time snapshot can show the wrong icon. The observer
  // keeps the icon honest no matter who flips the class.
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDark(document.documentElement.classList.contains('dark'));
    });
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const toggle = () => {
    const next = !isDark;
    setIsDark(next);
    // Glaze themes off [data-theme]; legacy Terra dark: off .dark — keep both in lockstep
    document.documentElement.classList.toggle('dark', next);
    document.documentElement.dataset.theme = next ? 'dark' : 'light';
    localStorage.setItem('theme', next ? 'dark' : 'light');
  };

  return (
    <button
      onClick={toggle}
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      className="p-1.5 rounded-lg text-nightshade-400 hover:text-nightshade-700 dark:hover:text-nightshade-200 hover:bg-nightshade-100 dark:hover:bg-nightshade-800 transition-colors"
    >
      {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
    </button>
  );
}
